import { generateJSON, mergeAttributes, Node, type JSONContent } from "@tiptap/core";
import { v4 as uuid } from "uuid";

import { setNodeViewAttrs } from "@/wc/editor/adapters/utils.ts";
import { getEditorInstanceId } from "@/wc/editor/utils/editor.ts";
import { findNodeWithPosition } from "@/wc/editor/utils/nodes.ts";

import { NOTE_CONTENT_UPDATE_EVENT, NoteType, type NWNoteProps } from "./models.ts";
import type { NWNote } from "./index.tsx";
import { NW_NOTE_CORE_EXTENSIONS } from "./extensions.ts";

export interface NWNoteOptions {
  HTMLAttributes: Record<string, string | boolean>;
}

type CreateNWNoteOptions = Partial<Omit<NWNoteProps, "content">> & { content?: JSONContent };

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    nwNote: {
      /**
       * Add a note
       */
      createNWNote: (options: CreateNWNoteOptions) => ReturnType;
      /**
       * Delete a note
       */
      deleteNote: (id: string) => ReturnType;
      /**
       * Update the heading of a note
       */
      updateNoteHeading: (id: string, heading: string) => ReturnType;
      /**
       * Update the content of a note (sets the content attribute)
       * Accepts JSONContent
       */
      updateNoteContent: (id: string, content: JSONContent) => ReturnType;
    };
  }
}

export const NWNoteAdapter = Node.create<NWNoteOptions>({
  name: "nw-note",
  group: "block",
  atom: true,
  draggable: false,
  selectable: false,
  marks: "highlight",

  addAttributes() {
    return {
      id: {
        default: () => uuid(),
        parseHTML: element => element.getAttribute("id"),
      },
      notetype: {
        default: NoteType.BLANK,
        parseHTML: element => element.getAttribute("notetype") || NoteType.BLANK,
      },
      heading: {
        default: "",
        parseHTML: element => element.getAttribute("heading"),
      },
      highlightContext: {
        default: null,
        parseHTML: element => element.getAttribute("highlightContext"),
      },
      surroundingContext: {
        default: null,
        parseHTML: element => element.getAttribute("surroundingContext"),
      },
      group: {
        default: null,
        parseHTML: element => element.getAttribute("group"),
      },
      loading: {
        default: false,
        parseHTML: element => element.getAttribute("loading") === "true",
        renderHTML: attributes => ({ loading: attributes.loading ? "true" : undefined }),
      },
      error: {
        default: null,
        parseHTML: element => element.getAttribute("error"),
      },
      query: {
        default: null,
        parseHTML: element => element.getAttribute("query"),
      },
      // Content attribute stores JSONContent for inner editor to hydrate from
      content: {
        default: null,
        parseHTML: element => {
          const contentAttr = element.getAttribute("data-initial-content");

          if (contentAttr) {
            try {
              return JSON.parse(contentAttr);
            } catch (e) {
              console.error(
                `[NWNoteAdapter parseHTML] Error parsing nw-note content JSON from data-initial-content for note ${element.getAttribute("id")}:`,
                e,
              );
              return null;
            }
          }

          // If no data-initial-content but has children, convert children to JSONContent. Accounts for backward compatibility with old notes.
          if (element.childNodes.length > 0) {
            try {
              return generateJSON(element.innerHTML, NW_NOTE_CORE_EXTENSIONS());
            } catch (e) {
              console.error(
                `[NWNoteAdapter parseHTML] Error converting HTML to JSONContent for note ${element.getAttribute("id")}:`,
                e,
              );
              return null;
            }
          }

          return null;
        },
        renderHTML: attributes => {
          if (attributes.content) {
            try {
              // Store the stringified JSON in data-initial-content for initial hydration
              return { "data-initial-content": JSON.stringify(attributes.content) };
            } catch (e) {
              console.error(`[NWNoteAdapter renderHTML] Error stringifying content JSON for note ${attributes.id}:`, e);
            }
          }
          return {};
        },
        toJSON: (attributes: Record<string, unknown>) => {
          return attributes.content;
        },
        fromJSON: (jsonAttribute: unknown) => {
          if (jsonAttribute && typeof jsonAttribute === "object") {
            return jsonAttribute;
          }

          return null;
        },
      },
      placeholder: {
        default: null,
        parseHTML: element => element.getAttribute("placeholder"),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "nw-note",
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const merged = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes);

    // Handle content rendering
    if (node.attrs.content) {
      try {
        // Store the stringified JSON in data-initial-content for initial hydration
        merged["data-initial-content"] = JSON.stringify(node.attrs.content);
      } catch (e) {
        console.error(`[NWNoteAdapter renderHTML] Error stringifying content JSON for note ${node.attrs.id}:`, e);
        delete merged["data-initial-content"];
      }
    } else {
      delete merged["data-initial-content"];
    }

    return ["nw-note", merged];
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const dom = document.createElement("nw-note") as NWNote;

      dom._editorInstanceId = getEditorInstanceId(editor);
      // Pass initial content via property for the component's first render
      dom._initialContentForEditor = node.attrs.content;

      const processContentUpdate = (newContent: JSONContent) => {
        if (typeof getPos !== "function" || !editor.view || editor.isDestroyed) {
          console.warn("[NWNoteAdapter] getPos function or editor view not available during content update.");
          return;
        }

        let currentPos: number | undefined;
        try {
          currentPos = getPos();
        } catch (error) {
          console.warn("[NWNoteAdapter] Error getting position during content update:", error);
          return;
        }

        if (currentPos !== undefined && currentPos >= 0) {
          // Map the position through transactions that might have happened since the update was triggered
          const resolvedPos = editor.state.tr.mapping.map(currentPos);
          const nodeAtPos = editor.state.doc.nodeAt(resolvedPos);

          if (nodeAtPos && nodeAtPos.type.name === this.name) {
            const currentContent = nodeAtPos.attrs.content;
            // Only dispatch a transaction if the content has actually changed
            if (JSON.stringify(currentContent) !== JSON.stringify(newContent)) {
              const tr = editor.state.tr.setNodeAttribute(resolvedPos, "content", newContent);
              // Set meta to prevent this transaction from being added to the undo history if needed,
              // though internal edits probably *should* be undoable. Consider removing this meta.
              // tr.setMeta("addToHistory", false);
              editor.view.dispatch(tr);
            }
          } else {
            console.warn(
              `[NWNoteAdapter] Node not found or type mismatch at resolved position ${resolvedPos} (original: ${currentPos}) during content update. Node:`,
              nodeAtPos,
            );
          }
        } else {
          console.warn(`[NWNoteAdapter] Invalid position (${currentPos}) obtained during content update.`);
        }
      };

      const handleInternalUpdate = (event: Event) => {
        if (event instanceof CustomEvent && event.type === NOTE_CONTENT_UPDATE_EVENT) {
          const newContentJson = event.detail as JSONContent;
          processContentUpdate(newContentJson);
        }
      };

      // Set initial attributes, excluding content which is handled via _initialContentForEditor
      const { _content, ...otherAttrs } = node.attrs;
      setNodeViewAttrs(dom, otherAttrs);

      dom.addEventListener(NOTE_CONTENT_UPDATE_EVENT, handleInternalUpdate);

      // Make the node non-editable from the outer editor's perspective
      dom.contentEditable = "false";

      return {
        dom,
        // Prevent outer editor from trying to manage content inside this node view
        ignoreMutation(mutation) {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName &&
            (dom.hasAttribute(mutation.attributeName) || mutation.attributeName === "data-initial-content")
          ) {
            // Let ProseMirror handle attribute updates from transactions
            return false;
          }
          // Ignore internal mutations (like childList changes within the component's internal editor)
          return (mutation.target !== dom && dom.contains(mutation.target)) || mutation.type === "childList";
        },

        // Stop ProseMirror event handling within the component to allow internal editor interaction
        stopEvent(event) {
          const target = event.target as globalThis.Node | null;

          if (!target) {
            return false;
          }

          const internalEditorContent = dom.querySelector(".ProseMirror");

          // If the target is within the internal editor's content area, stop the event
          // unless it's the content area itself (to allow focusing)
          // Ensure 'target' is a DOM Node before calling contains
          if (internalEditorContent && internalEditorContent.contains(target) && target !== internalEditorContent) {
            return true; // Stop propagation to outer editor, let internal editor handle it
          }

          return false;
        },

        update: updatedNode => {
          // If the node type changes, ProseMirror must re-render it.
          if (updatedNode.type !== node.type) {
            return false;
          }

          // Check which attributes changed
          const changedAttrs: string[] = [];
          for (const key in updatedNode.attrs) {
            let changed = false;
            if (key === "content") {
              changed = JSON.stringify(node.attrs.content) !== JSON.stringify(updatedNode.attrs.content);
            } else {
              // Use simple comparison for other attributes
              changed = node.attrs[key] !== updatedNode.attrs[key];
            }
            if (changed) {
              changedAttrs.push(key);
            }
          }

          if (changedAttrs.length === 0) {
            return true;
          }

          // Attributes that require a full re-render (return false)
          const forceReRenderAttrs = ["loading", "error", "notetype", "group", "id"]; // Add other critical attrs if needed

          const requiresReRender = changedAttrs.some(attr => forceReRenderAttrs.includes(attr));

          if (requiresReRender) {
            // Let ProseMirror handle the re-render by returning false.
            // Update the node reference *before* returning false.
            node = updatedNode;
            return false;
          }

          // If only non-critical attributes changed (e.g., 'content', 'heading', 'placeholder'),
          // update the component directly and return true.

          if (changedAttrs.includes("content")) {
            // Call updateContent specifically for content changes
            (dom as NWNote).updateContent(updatedNode.attrs.content);
          }

          // Update all other changed attributes directly on the DOM element using setNodeViewAttrs.
          // Filter attributes to only set the ones that actually changed and are not 'content'.
          const attrsToSet = Object.fromEntries(
            Object.entries(updatedNode.attrs).filter(([key]) => key !== "content" && changedAttrs.includes(key)),
          );

          if (Object.keys(attrsToSet).length > 0) {
            setNodeViewAttrs(dom, attrsToSet);
          }

          // Update the node reference for the next update cycle.
          node = updatedNode;

          // Return true: Component handled the update, PM should not re-render.
          return true;
        },

        destroy: () => {
          dom.removeEventListener(NOTE_CONTENT_UPDATE_EVENT, handleInternalUpdate);
        },
      };
    };
  },

  addCommands() {
    return {
      createNWNote:
        (options: CreateNWNoteOptions) =>
        ({ commands }) => {
          // Ensure initial content structure is provided (default to empty doc if JSON)
          const initialContent = options.content || { type: "doc", content: [{ type: "paragraph" }] };
          return commands.insertContent(
            {
              type: this.name,
              attrs: {
                ...options,
                content: initialContent,
              },
            },
            { updateSelection: true },
          );
        },

      deleteNote:
        id =>
        ({ state, tr, dispatch }) => {
          const result = findNodeWithPosition(state, this.name, id);

          if (result) {
            if (dispatch) {
              const transaction = tr.delete(result.pos, result.pos + result.node.nodeSize);
              dispatch(transaction);
            }
            return true;
          }

          console.warn(`Note with id ${id} not found.`);
          return false;
        },

      updateNoteHeading:
        (id, heading) =>
        ({ state, tr, dispatch }) => {
          const result = findNodeWithPosition(state, this.name, id);

          if (result) {
            const transaction = tr.setNodeAttribute(result.pos, "heading", heading);
            dispatch?.(transaction);
            return true;
          }
          return false;
        },

      updateNoteContent:
        (id: string, content: JSONContent) =>
        ({ state, tr, dispatch }) => {
          const result = findNodeWithPosition(state, this.name, id);

          if (result) {
            tr.setNodeAttribute(result.pos, "content", content);
            dispatch?.(tr);
            return true;
          }

          return false;
        },
    };
  },
});
